
/* eslint-disable */
import { AppAssessments } from "@/interfaces/data_objects/app_assessments";
import {
  APP_ENG_ASSESSMENT_COMPLETED,
  ASSESSMENT_QUESTIONNAIRES_ENGLISH,
  ASSESSMENT_QUESTIONNAIRES_ENGLISH_UPDATE,
  SUBMIT_APP_ASSESSMENT_ANSWERS,
  SUBMIT_APP_ASSESSMENT_ANSWER_MEDIA
} from "@/store/modules/candidates/constants";
import {
  SubmitAssessmentAnswersApiPayload,
  UpdateAssessmentQuestionPayload
} from "@/store/modules/candidates/interfaces";
import { ROOT_ERROR } from "@/store/modules/root/constants";
import { convert_seconds_to_minutes } from "@/utils/global";
import ErrorPage from "@/views/ErrorPage.vue";
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import AppEngWrittenAssessmentLoader from "./AppEngWrittenAssessmentLoader.vue";

function createOnMicRevoked(vm: {
  isRecording: boolean;
  recording: boolean;
  set_root_error: (msg: string) => void;
  $t: (key: string) => unknown;
}): () => void {
  return () => {
    if (vm.isRecording) {
      vm.isRecording = false;
      vm.recording = false;
      vm.set_root_error(
        String(vm.$t("assessments.permission-revoked"))
      );
    }
  };
}

export default Vue.extend({
  name: "AppEngSpeakingAssessment",
  components: {
    ErrorPage,
    AppEngWrittenAssessmentLoader
  },
  data() {
    return {
      assessment_type: "english_v4",
      current_question_index: 0,
      current_question: null as null | AppAssessments.AssessmentQuestionnaires,
      speaking_questions: [] as AppAssessments.AssessmentQuestionnaires[],
      submit_ans_loading: false,
      isRecording: false,
      recordedAudioBlob: null as Blob | null,
      recordedAudioUrl: "" as string,
      transcript: "" as string,
      mediaRecorder: null as MediaRecorder | null,
      audioChunks: [] as Blob[],
      recording: false,
      startTime: 0,
      elapsedTime: "0:0",
      // eslint-disable-next-line no-undef
      speechRecognition: null as typeof window.webkitSpeechRecognition | null
    };
  },
  computed: {
    ...mapGetters("candidate", {
      app_eng_assessments: ASSESSMENT_QUESTIONNAIRES_ENGLISH
    }),
    AppAssessments() {
      return AppAssessments;
    }
  },
  watch: {
    "app_eng_assessments.speaking": {
      handler(newVal) {
        if (Array.isArray(newVal) && newVal.length > 0 && !this.current_question) {
          this.loadSpeakingQuestions();
        }
      },
      deep: true
    }
  },
  mounted() {
    this.check_stage();
    this.loadSpeakingQuestions();
    this.initSpeechRecognition();
  },
  methods: {
    ...mapMutations("candidate", {
      update_app_eng_assessment_questions:
        ASSESSMENT_QUESTIONNAIRES_ENGLISH_UPDATE,
      set_app_eng_assessments: ASSESSMENT_QUESTIONNAIRES_ENGLISH,
      update_assessment_completed: APP_ENG_ASSESSMENT_COMPLETED
    }),
    ...mapMutations({
      set_root_error: ROOT_ERROR
    }),
    ...mapActions("candidate", {
      submit_assessment_answers: SUBMIT_APP_ASSESSMENT_ANSWERS,
      submit_assessment_ans_media: SUBMIT_APP_ASSESSMENT_ANSWER_MEDIA
    }),
    event_cancellation(event: Event) {
      event.preventDefault();
    },
    convert_seconds_to_minutes,
    loadSpeakingQuestions() {
      // Extract all speaking parts from the speaking array
      const rawSpeaking = this.app_eng_assessments?.speaking;
      if (!rawSpeaking || !Array.isArray(rawSpeaking)) {
        console.warn("[Speaking] No speaking data:", {
          hasSpeaking: !!rawSpeaking,
          isArray: Array.isArray(rawSpeaking),
          assessmentType: this.app_eng_assessments?.assessment_type
        });
        return;
      }

      this.speaking_questions = rawSpeaking
        .map((q: AppAssessments.AssessmentQuestionnaires) => {
          // Normalize question_options if it arrived as a JSON string
          if (typeof q.question_options === "string") {
            try {
              return {
                ...q,
                question_options: JSON.parse(
                  q.question_options
                ) as AppAssessments.QuestionOptions
              };
            } catch {
              return q;
            }
          }
          return q;
        })
        .filter((question: AppAssessments.AssessmentQuestionnaires) => {
          const opts = question.question_options;
          if (!opts) return false;
          const f =
            opts.filter ??
            (opts as { filter_ml?: string }).filter_ml;
          if (!f) return false;
          const filter = String(f).toLowerCase();
          return filter === "speaking" || filter.startsWith("speaking");
        });

      if (this.speaking_questions.length === 0 && rawSpeaking.length > 0) {
        console.warn("[Speaking] All questions filtered out:", {
          rawCount: rawSpeaking.length,
          sample: rawSpeaking[0]?.question_options
        });
      }

      if (this.speaking_questions.length > 0) {
        this.current_question = this.speaking_questions[0];
        this.current_question_index = 0;
      }
    },
    async check_stage() {
      const stage = localStorage.getItem("stage");
      this.assessment_type = this.app_eng_assessments.assessment_type;
      if (stage && stage === "SPEAKING_V4") {
        this.set_app_eng_assessments({
          speaking: this.app_eng_assessments.speaking,
          speaking_completed: this.app_eng_assessments.speaking_completed,
          written: this.app_eng_assessments.written,
          filter: this.app_eng_assessments.filter,
          reading_questions: this.app_eng_assessments.reading_questions,
          cancelled: false,
          completed: false,
          current_question_index: this.current_question_index,
          id: this.app_eng_assessments.id,
          assessment_type: this.app_eng_assessments.assessment_type
        });
        const savedQuestionIndex = localStorage.getItem("question_index");

        if (savedQuestionIndex) {
          this.current_question_index = parseInt(savedQuestionIndex);
        }
      }
    },
    initSpeechRecognition() {
      // eslint-disable-next-line no-undef
      const SpeechRecognition =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).SpeechRecognition ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        this.speechRecognition = new SpeechRecognition();
        this.speechRecognition.continuous = true;
        this.speechRecognition.interimResults = false;
        this.speechRecognition.lang = "en-US";
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.speechRecognition.onresult = (event: any) => {
          this.transcript = Array.from(event.results)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((result: any) => result[0])
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((result: any) => result.transcript)
            .join("");
        };
      }
    },
    async toggleRecording() {
      if (this.isRecording) {
        this.stopRecording();
      } else {
        await this.startRecording();
      }
    },
    async startRecording() {
      try {
        // Starting a new recording should invalidate any previous answer
        this.recordedAudioBlob = null;
        this.recordedAudioUrl = "";

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true
        });
        const audioTrack = stream.getAudioTracks()[0];
        audioTrack.addEventListener("ended", createOnMicRevoked(this as any));
        this.mediaRecorder = new MediaRecorder(new MediaStream([audioTrack]));
        this.audioChunks = [];
        this.transcript = "";

        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            this.audioChunks.push(event.data);
          }
        };

        this.mediaRecorder.onstop = () => {
          const audioBlob = new Blob(this.audioChunks, {
            type: "audio/webm"
          });

          this.recordedAudioBlob = audioBlob;
          this.recordedAudioUrl = URL.createObjectURL(audioBlob);
        };

        this.mediaRecorder.start();
        this.isRecording = true;
        this.recording = true;
        this.startTime = Date.now();
        this.updateTime();

        if (this.speechRecognition) {
          this.speechRecognition.start();
        }
      } catch (error) {
        console.error("Error accessing microphone:", error);
        this.set_root_error("Failed to access microphone");
      }
    },
    stopRecording() {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop();
        this.isRecording = false;
        this.recording = false;
        if (this.speechRecognition) {
          this.speechRecognition.stop();
        }
        this.mediaRecorder.stream.getTracks().forEach((track) => track.stop());
      }
    },
    clearRecording() {
      this.recordedAudioBlob = null;
      this.recordedAudioUrl = "";
      this.transcript = "";
      this.audioChunks = [];
      this.elapsedTime = "0:0";
    },
    updateTime() {
      if (this.recording) {
        const currentTime = Date.now();
        const elapsed = Math.floor((currentTime - this.startTime) / 1000);
        const minutes = Math.floor(elapsed / 60);
        const seconds = elapsed % 60;
        this.elapsedTime = `${minutes}:${seconds}`;
        setTimeout(() => this.updateTime(), 1000);
      }
    },
    isLastQuestion() {
      console.log("daf");
      return this.current_question_index >= this.speaking_questions.length - 1;
    },
    async process_next_question() {
      this.submit_ans_loading = true;
      if (!this.current_question || !this.recordedAudioBlob) return;

      // Update current question answer in store
      const payload: UpdateAssessmentQuestionPayload = {
        filter: this.app_eng_assessments.filter,
        answer: this.transcript || "Audio response",
        question_id: this.current_question.id
      };
      this.update_app_eng_assessment_questions(payload);

      // Api Call to save current question answer
      const api_payload: SubmitAssessmentAnswersApiPayload = {
        answer: this.transcript || "Audio response",
        question_id: this.current_question.id,
        answer_type: this.current_question.question_type,
        assessment_id: this.current_question.assessment_id,
        done:
          this.current_question_index >=
          this.app_eng_assessments.speaking.length - 1
      };

      const response = await this.submit_assessment_answers(api_payload);
      if (!response) {
        this.set_root_error(
          "Something went wrong while submitting assessment answers"
        );
        this.submit_ans_loading = false;
        return;
      }
      // Emit answered question id for global progress (english_v4)
      if (
        (this.assessment_type === "english_v4" ||
          this.assessment_type === "english_v5" ||
          this.assessment_type === "english_genx_easy_v1" ||
          this.assessment_type === "english_genx_medium_v1" ||
          this.assessment_type === "english_technician_v1" ||
          this.assessment_type === "english_lead_technician_v1" ||
          this.assessment_type === "english_assistant_engineer_v1") &&
        this.current_question?.id
      ) {
        this.$emit("question_answered", this.current_question.id);
      }
      // Upload audio file
      const ans_id = response.id;
      if (ans_id && this.recordedAudioBlob) {
        const formData = new FormData();
        const filename = `${this.app_eng_assessments.id}_ans_${ans_id}.wav`;
        const file = new File([this.recordedAudioBlob], filename, {
          type: "audio/wav",
          originalname: filename
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);
        formData.append("audio", file);
        formData.append("answer_id", ans_id.toString());
        formData.append(
          "assessment_id",
          this.app_eng_assessments.id.toString()
        );

        // Attempt audio upload with retry logic
        let uploadSuccess = false;
        const maxRetries = 2;
        for (
          let attempt = 0;
          attempt <= maxRetries && !uploadSuccess;
          attempt++
        ) {
          const uploadResult = await this.submit_assessment_ans_media(formData);
          if (uploadResult) {
            uploadSuccess = true;
          } else if (attempt < maxRetries) {
            // Wait before retry
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        }

        if (!uploadSuccess) {
          this.set_root_error(
            "Audio upload failed. Your response text was saved but audio recording may not be available for review."
          );
          // Continue to next question despite upload failure - answer text is saved
          // eslint-disable-next-line no-console
          console.error(
            `Audio upload failed for question_id: ${this.current_question.id}, answer_id: ${ans_id}`
          );
        }
      }

      // Navigate to next sub-question or part

      const isLastQuestion =
        this.current_question_index >=
        this.app_eng_assessments.speaking.length - 1;

      if (!isLastQuestion) {
        // Move to next sub-question in current part
        this.current_question_index++;
        this.current_question =
          this.app_eng_assessments.speaking[this.current_question_index];
        this.clearRecording();
        localStorage.setItem(
          "current_question_index",
          this.current_question_index.toString()
        );
      } else {
        // All parts and sub-questions completed
        this.set_app_eng_assessments({
          speaking: this.app_eng_assessments.speaking,
          speaking_completed: this.app_eng_assessments.speaking_completed,
          written: this.app_eng_assessments.written,
          reading_questions: this.app_eng_assessments.reading_questions,
          filter: this.app_eng_assessments.filter,
          cancelled: false,
          completed: true,
          current_question_index: this.current_question_index,
          id: this.app_eng_assessments.id,
          assessment_type: this.app_eng_assessments.assessment_type
        });
        localStorage.removeItem("stage");
        localStorage.removeItem("current_question_index");
        this.clearRecording();
        this.$emit("assessment_completed");
      }
      window.scrollTo({
        top: 0,
        behavior: "smooth" // optional, smooth scroll
      });
      this.submit_ans_loading = false;
    },
    handle_time_over() {
      // Stop recording when part time ends
      if (this.isRecording) {
        this.stopRecording();
      }
      if (!this.current_question) {
        this.set_root_error("Time over – no current question context.");
        return;
      }
      const isLastQuestion =
        this.current_question_index >= this.app_eng_assessments.speaking.length;

      // If this is the final sub-question of the final part, auto-submit and complete assessment
      if (isLastQuestion) {
        const answerText = this.transcript || "Audio response (timeout)";
        // Update store question answer (placeholder answer if none recorded)
        const payload: UpdateAssessmentQuestionPayload = {
          filter: this.app_eng_assessments.filter,
          answer: answerText,
          question_id: this.current_question.id
        };
        this.update_app_eng_assessment_questions(payload);

        const api_payload: SubmitAssessmentAnswersApiPayload = {
          answer: answerText,
          question_id: this.current_question.id,
          answer_type: this.current_question.question_type,
          assessment_id: this.current_question.assessment_id,
          done: true
        };

        this.submit_assessment_answers(api_payload).then(async (response) => {
          if (response && response.id && this.recordedAudioBlob) {
            const formData = new FormData();
            const file = new File(
              [this.recordedAudioBlob],
              `${this.app_eng_assessments.id}_ans_${response.id}.wav`,
              {
                type: "audio/wav",
                originalname: `answer_${response.id}.wav`
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            );
            formData.append("audio", file);
            formData.append("answer_id", response.id.toString());
            formData.append(
              "assessment_id",
              this.app_eng_assessments.id.toString()
            );

            // Attempt audio upload with retry logic
            let uploadSuccess = false;
            const maxRetries = 2;
            for (
              let attempt = 0;
              attempt <= maxRetries && !uploadSuccess;
              attempt++
            ) {
              const uploadResult = await this.submit_assessment_ans_media(
                formData
              );
              if (uploadResult) {
                uploadSuccess = true;
              } else if (attempt < maxRetries) {
                // Wait before retry
                await new Promise((resolve) => setTimeout(resolve, 1000));
              }
            }

            if (!uploadSuccess) {
              // eslint-disable-next-line no-console
              console.error(
                `Audio upload failed on timeout for question_id: ${this.current_question?.id}, answer_id: ${response.id}`
              );
            }
          }
          // Mark assessment completed
          this.set_app_eng_assessments({
            speaking: this.app_eng_assessments.speaking,
            speaking_completed: this.app_eng_assessments.speaking_completed,
            written: this.app_eng_assessments.written,
            reading_questions: this.app_eng_assessments.reading_questions,
            filter: this.app_eng_assessments.filter,
            cancelled: false,
            completed: true,
            current_question_index: this.current_question_index,
            id: this.app_eng_assessments.id,
            assessment_type: this.app_eng_assessments.assessment_type
          });
          localStorage.removeItem("stage");
          localStorage.removeItem("question_index");
          this.$emit("assessment_completed");
        });
        this.set_root_error("Time over – assessment auto-submitted.");
      } else {
        // Not final part: auto advance to next part
        if (!isLastQuestion) {
          this.current_question_index++;
          this.clearRecording();
          this.current_question =
            this.speaking_questions[this.current_question_index];
          localStorage.setItem(
            "question_index",
            this.current_question_index.toString()
          );
          this.set_root_error("Time over – moved to next part automatically.");
        } else {
          // Final part but not last sub-question: keep user on remaining sub-questions
          this.set_root_error(
            "Time over for this part. Complete remaining questions or submit."
          );
        }
      }
    }
  },
  beforeDestroy() {
    if (this.isRecording) {
      this.stopRecording();
    }
    if (this.speechRecognition) {
      this.speechRecognition.stop();
    }
  }
});
